
import Radar from '~/node_modules/radar-sdk-js'
import 'radar-sdk-js/dist/radar.css'
import maplibregl from 'maplibre-gl'
import { mapState } from 'vuex'

export default {
  data() {
    return {
      map: false,
      activeLocation: null,
      marker: 'https://storyblok.pleinaircdn.com/f/110020/121x140/53823ba39b/bo-map-marker.png',
      hideOrderMessage: false,
      cateringLocations: [],
      locations: [] // Initialize locations in data to handle it as an array
    }
  },
  mounted() {
    this.$nextTick(() => {
      // Initialize the map and fetch locations when the component is mounted
      this.initialize()
      this.fetchLocations() // Fetch locations and add markers once data is loaded
      // Add click event listener for the order button
      document.addEventListener('click', function (e) {
        if (e.target.hasAttribute('data-order')) {
          this.startOrder(this.activeLocation)
        }
      }.bind(this))

      // Add alt attribute to Radar logos
      this.addAltToRadarLogos()

      // Hide the order message after a delay
      setTimeout(() => {
        this.hideOrderMessage = true
      }, 4000)
    })
  },
  computed: {
    // Map Vuex state to local computed properties
    ...mapState(['locationList', 'allLocations', 'location', 'handoffToggle', 'channel']),

    // Computed property to detect mobile devices based on screen size
    mobile() {
      return this.$mq == 'sm' || this.$mq == 'md'
    }
  },
  methods: {
    // Initialize Radar map
    initialize() {
      Radar.initialize(process.env.RADAR_KEY)
      // Remove the map if it already exists to avoid duplicates
      const mapContainer = document.getElementById('map')

      if (!mapContainer) {
        console.error("Map container not found")
        return
      }

      if (this.map) {
        this.map.remove()
      }

      this.map = Radar.ui.map({
        container: 'map',
      })

      this.map.on('error', (err) => {
        console.error(err)
      })
    },

    // Fetch locations based on the current channel and store them in `locations`
    async fetchLocations() {
      if (this.channel !== 'catering') {
        // If not catering, use locationList or allLocations from Vuex
        if (this.locationList && this.locationList.length) {
          this.locations = this.locationList
        } else {
          this.locations = this.allLocations
        }
      } else {
        // For catering, fetch catering locations from an API call
        try {
          this.cateringLocations = await this.$api.findCateringLocations()
          this.locations = this.cateringLocations.locations
          console.log(this.locations)
        } catch (error) {
          console.error("Error fetching catering locations", error)
          this.locations = [] // Ensure locations is an array even on error
        }
      }
      this.addMarkers() // Add markers after locations are fetched
    },

    // Add markers to the map for each location
    addMarkers() {
      // Ensure locations is an array before iterating
      if (!Array.isArray(this.locations)) {
        console.error("locations is not an array", this.locations)
        return
      }

      // Loop through each location and add a marker to the map
      this.locations.forEach((location, i) => {
        const marker = document.createElement('div')
        marker.className = 'marker'
        marker.style.backgroundImage = 'url(' + this.marker + ')'
        marker.style.width = '42.494px'
        marker.style.height = '52px'
        marker.style.backgroundSize = '100%'
        marker.addEventListener('click', () => {
          this.setActiveLocation(location)
        })

        // Add marker to the map, different handling based on location type
        if (!location.key) { // Handle all locations
          new maplibregl.Marker(marker)
            .setLngLat([location.lng, location.lat])
            .addTo(this.map)
        } else { // Handle search results
          new maplibregl.Marker(marker)
            .setLngLat([location.lng, location.lat])
            .addTo(this.map)
        }

        // Center the map to the first search result
        if (i == 0 && this.locationList?.length) {
          this.map.setZoom(11)
          setTimeout(() => {
            this.map.setCenter([location.lng, location.lat])
          }, 250)
        }
      })
    },

    // Set the active location and update the map
    async setActiveLocation(location) {
      if (location.key) { // Fetch full location details if a key is provided
        location = await this.$api.getLocation(location.key, false, false, false)
      }
      this.$store.commit('setActiveLocation', location) // Commit to Vuex store
      this.activeLocation = location // Update the local active location

      // Animate the map to focus on the active location
      setTimeout(() => {
        this.map.flyTo({
          center: [location.lng, location.lat],
          zoom: 15,
          essential: true,
          duration: 1500
        })
      }, 10)
    },

    // Start an order based on the active location
    async startOrder(location) {
      this.$store.commit('setLocation', location)
      const menu = await this.$api.getMenu()
      if (this.order && this.order.menu_id != location.menu_id) {
        await this.$api.transferOrder()
      } else {
        await this.$api.createOrder()
      }

      // Redirect to the order page or menu based on the query
      if (menu) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          'event': 'select_location',
          'location_name': location.address,
          'location_address': location.name,
        });

        if (this.$route.query.order) {
          this.$router.push(`/order/type/?order=${this.$route.query.order}`)
        } else {
          this.$router.push('/order/')
        }
      }
    },

    // Add alt attribute to the Radar logos
    addAltToRadarLogos() {
      const radarLogos = document.querySelectorAll('#radar-map-logo img')
      radarLogos.forEach((logo) => {
        if (logo && !logo.getAttribute('alt')) {
          logo.setAttribute('alt', '')
        }
      })
    },

    // Use browser's geolocation API to fetch coordinates
    geoLocate() {
      if (navigator.geolocation) {
        this.$store.commit('setLoading', true)
        navigator.geolocation.getCurrentPosition(this.getCoordinates, this.getCoordinatesError)
      }
    },

    // Fetch locations based on the user's coordinates
    async getCoordinates(position) {
      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      }
      await this.$api.findLocations(coords)
    },

    // Handle errors during geolocation
    getCoordinatesError() {
      this.$store.commit('setLoading', false)
    },
    zoomInLocation(location, zoom = 11) {
      if (this.map && typeof this.map.setZoom === 'function') {
        this.map.setZoom(zoom)
        setTimeout(() => this.map.setCenter([location.lng, location.lat]), 250)
      }
    },
    flyToLocation(location, zoom = 15) {
      if (this.map && typeof this.map.flyTo === 'function') {
        this.map.flyTo({
          center: [location.lng, location.lat],
          zoom: 15,
          essential: true,
          duration: 1500
        })
      }
    }
  },
  watch: {
    // Watch for changes in the locations and update markers accordingly
    locations() {
      this.initialize()
      this.addMarkers()
      this.addAltToRadarLogos()
    },
    locationList() {
      if (this.locationList?.length) {
        this.locations = this.locationList
        this.zoomInLocation(this.locationList[0])
      }
    },
    location() {
      if (this.location) {
        this.flyToLocation(this.location)
      }
    }
  }
}
